import React, { useEffect, useMemo, useState } from 'react'
import { supabase } from './supabaseClient'
import { X, Search, Loader, AlertCircle, CheckCircle, Save, Package, Tag } from 'lucide-react'

import { C } from './theme'
import { CopyButton } from './CopyButton'
import { lerTudo } from './lerTudo'

const fmt2 = (n) => Number(n || 0).toFixed(2)

// Calcula novo preço dado o operador e valor.
function calcularNovoPreco(precoAtual, op, val) {
  const p = Number(precoAtual || 0)
  const v = Number(val || 0)
  if (Number.isNaN(p) || Number.isNaN(v)) return p
  switch (op) {
    case 'percent_up':   return Math.round(p * (1 + v / 100) * 100) / 100
    case 'percent_down': return Math.round(p * (1 - v / 100) * 100) / 100
    case 'add':          return Math.round((p + v) * 100) / 100
    case 'subtract':     return Math.round((p - v) * 100) / 100
    case 'set':          return Math.round(v * 100) / 100
    default:             return p
  }
}

export function EdicaoMassaPrecos({ open, onClose, onUpdated }) {
  const [produtos, setProdutos] = useState([])
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState('')
  const [search, setSearch] = useState('')
  const [filtroCat, setFiltroCat] = useState('todas')
  const [selecionados, setSelecionados] = useState(new Set())
  const [op, setOp] = useState('percent_up')
  const [valor, setValor] = useState('')
  const [savingMsg, setSavingMsg] = useState({ tipo: '', texto: '' })
  const [salvando, setSalvando] = useState(false)
  const [coluna, setColuna] = useState('preco') // 'preco' (venda) | 'preco_custo'

  useEffect(() => {
    if (open) {
      setSelecionados(new Set())
      setValor('')
      setSavingMsg({ tipo: '', texto: '' })
      carregar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const carregar = async () => {
    setLoading(true)
    setErro('')
    // Produtos (sem custo) + tabela isolada (custo) em paralelo
    // Paginado (src/lerTudo.js): este ecra edita precos em massa e uma leitura
    // cortada nas 1000 linhas escondia metade do catalogo sem dizer nada.
    const [prodRes, internosRes] = await Promise.all([
      lerTudo(() => supabase.from('Produtos')
        .select('id, Nome, EAN, preco, categoria, foto_url, visivel_catalogo'),
        'Nome'),
      lerTudo(() => supabase.from('produtos_internos')
        .select('produto_nome, preco_custo'),
        'produto_nome'),
    ])
    const error = prodRes.error || internosRes.error
    let data = null
    if (!error) {
      const mapaCustos = new Map((internosRes.data || []).map(i => [i.produto_nome, Number(i.preco_custo)]))
      data = (prodRes.data || []).map(p => ({
        ...p,
        preco_custo: mapaCustos.has(p.Nome) ? mapaCustos.get(p.Nome) : null,
      }))
    }
    if (error) setErro('Erro ao carregar: ' + error.message)
    else setProdutos(data || [])
    setLoading(false)
  }

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase()
    return produtos.filter(p => {
      if (filtroCat !== 'todas' && p.categoria !== filtroCat) return false
      if (q && !(p.Nome || '').toLowerCase().includes(q) && !String(p.EAN || '').includes(q)) return false
      return true
    })
  }, [produtos, search, filtroCat])

  const todosSelecionados = filtrados.length > 0 && filtrados.every(p => selecionados.has(p.id))

  const toggleSel = (id) => {
    setSelecionados(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  const toggleSelAll = () => {
    if (todosSelecionados) {
      setSelecionados(prev => {
        const next = new Set(prev)
        filtrados.forEach(p => next.delete(p.id))
        return next
      })
    } else {
      setSelecionados(prev => {
        const next = new Set(prev)
        filtrados.forEach(p => next.add(p.id))
        return next
      })
    }
  }

  // Valor da coluna alvo no produto (preco ou preco_custo)
  const valorAtual = (p) => coluna === 'preco_custo' ? p.preco_custo : p.preco
  const previewNovoPreco = (p) => calcularNovoPreco(valorAtual(p), op, valor)

  const aplicar = async () => {
    if (selecionados.size === 0) { setSavingMsg({ tipo: 'erro', texto: 'Nada selecionado.' }); return }
    if (!valor || Number.isNaN(Number(valor))) { setSavingMsg({ tipo: 'erro', texto: 'Indica um valor numérico.' }); return }
    const ids = Array.from(selecionados)

    // Para preco_custo, alguns produtos podem estar a NULL — esses ficam de fora
    const elegiveis = produtos.filter(p => selecionados.has(p.id) && valorAtual(p) != null)
    const ignorados = produtos.filter(p => selecionados.has(p.id) && valorAtual(p) == null).length
    if (elegiveis.length === 0) {
      setSavingMsg({ tipo: 'erro', texto: 'Nenhum dos selecionados tem valor para esta coluna.' })
      return
    }
    const labelColuna = coluna === 'preco_custo' ? 'preço de custo' : 'preço de venda'
    const aviso = ignorados > 0 ? ` (${ignorados} sem valor — ignorados)` : ''
    if (!window.confirm(`Aplicar a ${elegiveis.length} produto(s)${aviso}? Esta ação altera o ${labelColuna} imediatamente.`)) return

    setSalvando(true)
    setSavingMsg({ tipo: '', texto: '' })
    try {
      for (const p of elegiveis) {
        const novoValor = calcularNovoPreco(valorAtual(p), op, valor)
        let error
        if (coluna === 'preco_custo') {
          // Tabela isolada (admin only)
          ;({ error } = await supabase.from('produtos_internos')
            .upsert({ produto_nome: p.Nome, preco_custo: novoValor }, { onConflict: 'produto_nome' }))
        } else {
          // Tabela Produtos (preço de venda)
          ;({ error } = await supabase.from('Produtos').update({ preco: novoValor }).eq('id', p.id))
        }
        if (error) throw new Error(error.message)
      }
      setSavingMsg({ tipo: 'ok', texto: `${elegiveis.length} ${labelColuna}(s) atualizado(s)${aviso}.` })
      setSelecionados(new Set())
      setValor('')
      await carregar()
      onUpdated?.()
    } catch (err) {
      setSavingMsg({ tipo: 'erro', texto: 'Erro: ' + (err?.message || String(err)) })
    } finally {
      setSalvando(false)
    }
  }

  if (!open) return null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 800, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ background: C.surface, borderRadius: '8px', width: '100%', maxWidth: '900px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: C.text, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Tag size={16} color={C.primary} /> Edição em massa de preços
            </h2>
            <p style={{ margin: '3px 0 0', fontSize: '12px', color: C.textMuted }}>Escolhe a coluna (venda ou custo), a operação e aplica a vários produtos de uma vez.</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}>
            <X size={18} />
          </button>
        </div>

        {/* Operações */}
        <div style={{ padding: '14px 20px', background: C.bg, borderBottom: `1px solid ${C.border}`, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Coluna:</span>
          <select value={coluna} onChange={e => { setColuna(e.target.value); setSelecionados(new Set()) }}
            style={{ height: '32px', padding: '0 10px', border: `1px solid ${coluna === 'preco_custo' ? C.warning : C.border}`, borderRadius: '4px', fontSize: '13px', background: coluna === 'preco_custo' ? '#fff8e1' : C.surface, cursor: 'pointer', fontWeight: '600' }}>
            <option value="preco">💶 Preço de venda</option>
            <option value="preco_custo">💸 Preço de custo</option>
          </select>
          <span style={{ fontSize: '12px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginLeft: '6px' }}>Operação:</span>
          <select value={op} onChange={e => setOp(e.target.value)}
            style={{ height: '32px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', background: C.surface, cursor: 'pointer' }}>
            <option value="percent_up">Aumentar %</option>
            <option value="percent_down">Diminuir %</option>
            <option value="add">Somar €</option>
            <option value="subtract">Subtrair €</option>
            <option value="set">Definir como €</option>
          </select>
          <input type="number" step="0.01" value={valor} onChange={e => setValor(e.target.value)} placeholder="Valor"
            style={{ height: '32px', width: '100px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px' }} />
          <span style={{ fontSize: '12px', color: C.textMuted }}>
            {op === 'percent_up' || op === 'percent_down' ? '%' : '€'}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: '12px', color: C.text, fontWeight: '600' }}>
            {selecionados.size} selecionado(s)
          </span>
          <button onClick={aplicar} disabled={salvando || selecionados.size === 0 || !valor}
            style={{ height: '32px', padding: '0 14px', border: 'none', borderRadius: '4px', background: C.primary, color: '#fff', cursor: (salvando || selecionados.size === 0 || !valor) ? 'not-allowed' : 'pointer', opacity: (salvando || selecionados.size === 0 || !valor) ? 0.5 : 1, fontSize: '12px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {salvando ? <><Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> A aplicar…</> : <><Save size={12} /> Aplicar</>}
          </button>
        </div>

        {savingMsg.texto && (
          <div style={{ padding: '8px 20px', background: savingMsg.tipo === 'ok' ? '#dff6dd' : '#fde7e9', color: savingMsg.tipo === 'ok' ? C.success : C.danger, fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {savingMsg.tipo === 'ok' ? <CheckCircle size={13} /> : <AlertCircle size={13} />} {savingMsg.texto}
          </div>
        )}

        {/* Filtros */}
        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 220px' }}>
            <Search size={13} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: C.textMuted }} />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Procurar nome ou EAN…"
              style={{ width: '100%', height: '32px', padding: '0 10px 0 30px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box' }} />
          </div>
          <select value={filtroCat} onChange={e => setFiltroCat(e.target.value)}
            style={{ height: '32px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', background: C.surface, cursor: 'pointer' }}>
            <option value="todas">Todas categorias</option>
            <option value="perfumes">Perfumes</option>
            <option value="cosmeticos">Cosméticos</option>
          </select>
          <button onClick={toggleSelAll}
            style={{ height: '32px', padding: '0 12px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, cursor: 'pointer', fontSize: '12px', color: C.text, fontWeight: '600' }}>
            {todosSelecionados ? 'Desmarcar todos' : `Selecionar todos (${filtrados.length})`}
          </button>
        </div>

        {/* Tabela */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>A carregar…</div>
          ) : erro ? (
            <div style={{ padding: '20px', color: C.danger }}>{erro}</div>
          ) : filtrados.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>
              <Package size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
              <p>Nenhum produto encontrado.</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead style={{ position: 'sticky', top: 0, background: C.bg, zIndex: 1 }}>
                <tr>
                  <th style={{ padding: '8px 16px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, width: '32px' }}></th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', borderBottom: `1px solid ${C.border}`, fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Produto</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', borderBottom: `1px solid ${C.border}`, fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{coluna === 'preco_custo' ? 'Custo atual' : 'Preço atual'}</th>
                  <th style={{ padding: '8px 16px', textAlign: 'right', borderBottom: `1px solid ${C.border}`, fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{coluna === 'preco_custo' ? 'Custo novo' : 'Preço novo'}</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map(p => {
                  const sel = selecionados.has(p.id)
                  const atual = valorAtual(p)
                  const novoPreco = sel && valor !== '' && atual != null ? previewNovoPreco(p) : null
                  const delta = novoPreco !== null ? novoPreco - Number(atual || 0) : 0
                  const semValor = atual == null
                  return (
                    <tr key={p.id} onClick={() => toggleSel(p.id)}
                        style={{ cursor: 'pointer', background: sel ? '#eff6fc' : 'transparent', borderBottom: `1px solid ${C.border}`, opacity: semValor ? 0.55 : 1 }}>
                      <td style={{ padding: '8px 16px' }}>
                        <input type="checkbox" checked={sel} readOnly
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ fontWeight: '600', color: C.text }}>{p.Nome}</div>
                        <div style={{ fontSize: '11px', color: C.textMuted }}>
                          {p.EAN ? `EAN ${p.EAN}` : 'Sem EAN'}
                          {p.EAN && <CopyButton text={p.EAN} size={11} title="Copiar EAN" />}
                          {p.categoria && ` · ${p.categoria}`}
                          {!p.visivel_catalogo && ' · arquivado'}
                          {semValor && coluna === 'preco_custo' && <span style={{ color: C.warning, fontWeight: '700' }}> · sem custo</span>}
                        </div>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', color: C.text }}>{semValor ? '—' : `${fmt2(atual)} €`}</td>
                      <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                        {novoPreco !== null ? (
                          <div>
                            <span style={{ fontWeight: '700', color: C.primary }}>{fmt2(novoPreco)} €</span>
                            {delta !== 0 && (
                              <span style={{ marginLeft: '6px', fontSize: '11px', color: delta > 0 ? C.success : C.danger, fontWeight: '600' }}>
                                {delta > 0 ? '+' : ''}{fmt2(delta)} €
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: C.textMuted, fontSize: '11px' }}>—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
